import { isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { useTRPC } from './useTRPC';

type CategoryInfo = RightResult<RouterOutputs['getDappCategoryInfo']>;
type StoreCategory = RightResult<RouterOutputs['listStoreCategories']>[number];

export interface UseDappCategoriesResult {
  info: CategoryInfo | null;
  isLoading: boolean;
  error: string | null;
  availableCategories: StoreCategory[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  selectedCategoryId: string;
  setSelectedCategoryId: (id: string) => void;
  isSaving: boolean;
  handleAssign: () => Promise<void>;
}

/**
 * Loads a dapp's current store category + ingestion status, and (when editing
 * is allowed) the list of assignable categories plus an assign action. Backing
 * the editor on the admin/moderator dapp detail page and the read-only list on
 * the publisher details page.
 */
export function useDappCategories(
  dappId: string | null,
  options: { canEdit: boolean },
): UseDappCategoriesResult {
  const trpc = useTRPC();
  const { canEdit } = options;

  const [info, setInfo] = useState<CategoryInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<
    StoreCategory[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!dappId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const result = await trpc.getDappCategoryInfo.query({ dappId });
        if (cancelled) {
          return;
        }
        if (isRight(result)) {
          setInfo(result.right);
        } else {
          setError(result.left.message || 'Failed to load categories');
        }
      } catch (loadError) {
        captureAndReportError('Failed to load dapp categories', loadError);
        if (!cancelled) {
          setError('Failed to load categories');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dappId, trpc]);

  useEffect(() => {
    if (!canEdit) {
      return;
    }

    let cancelled = false;
    setCategoriesLoading(true);
    setCategoriesError(null);

    void (async () => {
      try {
        const result = await trpc.listStoreCategories.query(undefined);
        if (cancelled) {
          return;
        }
        if (isRight(result)) {
          setAvailableCategories(result.right);
        } else {
          setCategoriesError(
            result.left.message || 'Failed to load categories',
          );
        }
      } catch (loadError) {
        captureAndReportError('Failed to load store categories', loadError);
        if (!cancelled) {
          setCategoriesError('Failed to load categories');
        }
      } finally {
        if (!cancelled) {
          setCategoriesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canEdit, trpc]);

  useEffect(() => {
    const currentCategoryName = info?.categories[0];
    const currentCategory = availableCategories.find(
      (category) => category.name === currentCategoryName,
    );
    setSelectedCategoryId(currentCategory?.id ?? '');
  }, [availableCategories, info?.categories]);

  const handleAssign = useCallback(async () => {
    if (!dappId || !selectedCategoryId) {
      return;
    }

    setIsSaving(true);
    try {
      const result = await trpc.setDappCategory.mutate({
        dappId,
        categoryId: selectedCategoryId,
      });
      if (isRight(result)) {
        const assigned = result.right[0];
        toast.success(
          assigned ? `Category set to ${assigned.name}` : 'Category updated',
        );
        setSelectedCategoryId(assigned?.id ?? selectedCategoryId);
        setInfo((prev) =>
          prev
            ? {
                ...prev,
                categories: result.right.map((category) => category.name),
              }
            : prev,
        );
      } else {
        toast.error(result.left.message || 'Failed to assign category');
      }
    } catch (assignError) {
      captureAndReportError('Failed to assign category', assignError);
      toast.error('Failed to assign category');
    } finally {
      setIsSaving(false);
    }
  }, [trpc, dappId, selectedCategoryId]);

  return {
    info,
    isLoading,
    error,
    availableCategories,
    categoriesLoading,
    categoriesError,
    selectedCategoryId,
    setSelectedCategoryId,
    isSaving,
    handleAssign,
  };
}
